import { ApiClient } from '@center-technologies/shared-api-clients';

/**
 * Interface representing the structure of the image response from the backend.
 */
interface SphereImageResponse {
  status: string;
  image: {
    id: string;
    imageUrl: string;
    thumbnailUrl: string;
    expiresAt: number;
  };
}

/**
 * Interface representing cached image URLs with their expiry time.
 */
interface CachedImageUrls {
  imageUrl: string;
  thumbnailUrl: string;
  expiresAt: number; // Unix timestamp in milliseconds
}

/**
 * In-memory cache for storing signed URLs.
 * Key: sphereId
 * Value: CachedImageUrls
 */
const imageCache: Map<string, CachedImageUrls> = new Map();

/**
 * Fetches signed image URLs for a given sphereId.
 * Utilizes caching to avoid redundant requests.
 *
 * @param apiClient - An ApiClient configured for your Graph endpoint
 * @param sphereId - The ID of the sphere.
 * @returns An object containing imageUrl, thumbnailUrl, and expiresAt timestamp.
 */
export async function getSignedSphereImage(
  apiClient: ApiClient,
  sphereId: string
): Promise<{ imageUrl: string; thumbnailUrl: string; expiresAt: number }> {
  // Check if URLs are cached and still valid
  const cached = imageCache.get(sphereId);
  const now = Date.now();

  if (cached && cached.expiresAt > now) {
    return {
      imageUrl: cached.imageUrl,
      thumbnailUrl: cached.thumbnailUrl,
      expiresAt: cached.expiresAt,
    };
  }

  // If not cached or expired, fetch from backend
  try {
    const data = await apiClient.get<SphereImageResponse>(
      `/spheres/${sphereId}/image`
    );

    if (data.status !== 'success') {
      throw new Error('Failed to fetch sphere image.');
    }

    const { imageUrl, thumbnailUrl, expiresAt } = data.image;

    // Cache the URLs with expiry
    imageCache.set(sphereId, { imageUrl, thumbnailUrl, expiresAt });

    return { imageUrl, thumbnailUrl, expiresAt };
  } catch (error: any) {
    console.error(
      `Error fetching signed image URLs for sphere ${sphereId}:`,
      error
    );
    throw new Error(
      error.response?.data?.error || 'Unable to retrieve sphere image.'
    );
  }
}

/**
 * Clears the cache for a specific sphereId.
 *
 * @param sphereId - The ID of the sphere.
 */
export function clearSphereImageCache(sphereId: string): void {
  imageCache.delete(sphereId);
}

/**
 * Clears the entire image cache.
 */
export function clearAllImageCache(): void {
  imageCache.clear();
}